import React, { useEffect, useState } from 'react';
import Axios from 'axios';
import clsx from 'clsx';
import { Drawer } from 'vaul';
import * as Routes from '@/modules/routes';
import SearchResults from '@/components/shared/Search/SearchResults';
import { ISearchResults } from '@/components/shared/Search/types';
import useSearchStore from '@/components/shared/Search/useSearchStore';
import { Button } from '@/components/ui/Buttons';
import ReactSelect5 from '@/components/common/ReactSelect5/ReactSelect5';

interface SimilarRecordsProps {
  modelId: number;
  modelType: 'SectionReadingLevelTranslation';
}

const SimilarRecords = ({ modelId, modelType }: SimilarRecordsProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [minimumShouldMatch, setMinimumShouldMatch] = useState('75%');
  const numResults = useSearchStore(state => state.numResults);
  const setError = useSearchStore(state => state.setError);
  const isLoading = useSearchStore(state => state.isLoading);
  const setIsLoading = useSearchStore(state => state.setIsLoading);
  const setResults = useSearchStore(state => state.setResults);
  const setNumResults = useSearchStore(state => state.setNumResults);
  const setSuggestions = useSearchStore(state => state.setSuggestions);

  const dropdownValue = {
    label: minimumShouldMatch,
    value: minimumShouldMatch,
  };

  const dropdownOptions = Array.from({ length: 21 }, (_, i) => {
    const value = `${i * 5}%`;
    return { label: value, value };
  }).sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));

  const fetchSimilarRecords = () => {
    if (isLoading) return;

    setNumResults(0);
    setError(false);
    setIsLoading(true);

    Axios.post<ISearchResults>(
      Routes.plato_api_similar_path({
        minimum_should_match: minimumShouldMatch,
        model_id: modelId,
        model_type: modelType,
      }),
    ).then((response) => {
      setResults(response?.data?.results);
      setNumResults(response?.data?.numResults);
      setSuggestions(response?.data?.suggestions);
      setIsLoading(false);
      setError(false);
    });
  };

  useEffect(() => {
    if (!isOpen) return;

    fetchSimilarRecords();
  }, [isOpen, minimumShouldMatch]);

  return (
    <>
      <Drawer.Root
        dismissible
        handleOnly
        modal
        direction="right"
        open={isOpen}
        onOpenChange={(open: boolean) => {
          setIsOpen(open);
        }}
      >
        <Drawer.Trigger asChild>
          <Button
            outlineStyle
            small
            className="tw-px-2 tw-absolute tw-right-0"
            type="button"
            onClick={() => setIsOpen(true)}
          >
            <i className="fa fa-search" /> Similar Sections
          </Button>
        </Drawer.Trigger>
        <Drawer.Portal>
          <Drawer.Overlay
            className="tw-fixed tw-inset-0 tw-bg-black/40 tw-z-9999"
            onClick={() => setIsOpen(false)}
          />
          <Drawer.Content
            className={clsx(
              'tw-right-2 tw-top-2 tw-bottom-2 tw-fixed tw-outline-none tw-w-[46rem]',
              'tw-z-[99999] tw-rounded-2xl tw-overflow-hidden',
            )}
          >
            <Drawer.Handle
              className="!tw-fixed -tw-left-4 !tw-top-1/2 tw-w-2 tw-bg-gray-300 tw-rotate-90"
            />
            <div className="card tw-bg-white tw-w-full tw-h-full tw-flex tw-flex-col tw-items-start tw-select-text">
              <Drawer.Title
                className={clsx(
                  'tw-mb-2 tw-border-solid tw-border-b tw-border-grey',
                  'tw-font-bold tw-p-5 tw-w-full tw-box-border',
                )}
              >
                Similar Content Search
              </Drawer.Title>
              <Drawer.Close asChild>
                <Button
                  outlineStyle
                  small
                  className="tw-absolute tw-text-lg tw-right-4 tw-top-4 tw-border-none"
                  type="button"
                  onClick={() => setIsOpen(false)}
                >
                  ✕
                </Button>
              </Drawer.Close>
              <div
                className={clsx(
                  'tw-h-full tw-w-full tw-grow tw-py-5 tw-flex tw-flex-col',
                  'tw-overflow-scroll tw-px-5 tw-box-border',
                )}
              >
                {numResults > 0 && (
                  <div className="tw-flex tw-flex-row tw-justify-between tw-items-center tw-w-full">
                    <div className="tw-mb-2">
                      {numResults} similar sections found
                    </div>
                    <div className="tw-w-48 tw-hidden">
                      <ReactSelect5
                        isSearchable={false}
                        options={dropdownOptions}
                        placeholder="Minimum Should Match"
                        value={dropdownValue}
                        onChange={(option: DropdownOption | null) => setMinimumShouldMatch(option?.value || '75%')}
                      />
                    </div>
                  </div>
                )}
                <SearchResults
                  showScore
                  truncateResults
                  itemsPerPage={10}
                />
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </>
  );
};

export default SimilarRecords;
